import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { organizationSchema, localBusinessSchema, websiteSchema } from "@/i18n/schemaData";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  CreditCard, Globe, LinkIcon, Shield,
  Smartphone, BarChart3, ArrowRight
} from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import HeroCarousel from "@/components/HeroCarousel";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const TERMINALS = [
  { slug: "ams1", name: "AMS1", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL3Q1Y2VySmpjV1B0UkpEZUR4a21SLnBuZyJ9:adyen:Kxr1RJJmlEr_VW--jzZVrMXBbA9jeCcRU6mQQNcP9KA?format=webp&width=400&height=530", tagKey: "mobile" },
  { slug: "sfo1", name: "SFO1", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL2lRTnNmMkpZbXlpRHNZU3p3dmdOLnBuZyJ9:adyen:_IvTntqAuPTeu_ZpRw2fA7TdLa_vOz1SyMVixX0xI_c?format=webp&width=400&height=530", tagKey: "counter" },
  { slug: "nyc1", name: "NYC1", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL2IzREdwSzRzMmpSV0NYZFJteHo5LnBuZyJ9:adyen:PItds0nupqldFNFSHXLFJvhoGof3pmEmtuxz-1lEsu4?format=webp&width=400&height=530", tagKey: "mobile" },
  { slug: "s1f2", name: "S1F2", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL01hNHR4a3Fib29xbThtZ1VmY3JXLnBuZyJ9:adyen:0fcuuj2UrJ36lqGND2gZAKWNaTUWzI6sHcSO-iawPJU?format=webp&width=400&height=530", tagKey: "mobile" },
  { slug: "m450", name: "M450", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcLzN4WlVLNTRTRDNNNThLTHZMRGtMLnBuZyJ9:adyen:zbH86lvJdp8wb4wEN_dwe02ZPFy9S-bxaQ-2UlpPv9w?format=webp&width=400&height=530", tagKey: "counter" },
  { slug: "s1e2l", name: "S1E2L", image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL1djMVFDTTF0U2NHTDZ4Sml3N2o0LnBuZyJ9:adyen:_zMomdQWR15y5m7MQvq7qmiksXeVXx1LNgi74Chotps?format=webp&width=400&height=530", tagKey: "mobile" },
];

const TESTIMONIALS = [
  { name: "La Ruota", role: "Geneve" },
  { name: "Fenomen Market Sarl", role: "Biel" },
  { name: "Al Quadrato", role: "Geneve" },
  { name: "Back-Office Restaurant", role: "Geneve" },
  { name: "Cafe-Restaurant HOA SEN", role: "Lausanne" },
  { name: "Ristorante Pizzeria Al Capone", role: "Biel" },
];

const TESTIMONIALS_TEXT = {
  fr: ["AyaPay a transforme notre facon de gerer les paiements. Le terminal gratuit et le support 24/7 font toute la difference.", "Depuis qu'on utilise AyaPay, nos transactions sont plus rapides et nos clients sont ravis. Le remplacement sous 24h c'est top.", "L'integration avec notre caisse est parfaite. Zero souci depuis l'installation, et la SIM 5G Swisscom assure une connexion permanente.", "Le fait que le terminal soit gratuit et tout compris nous a convaincu immediatement. Pas de surprise sur la facture.", "Service impeccable. L'equipe AyaPay est toujours disponible, meme le week-end. On recommande a 100%.", "On a essaye plusieurs fournisseurs avant AyaPay. Aucun ne propose un service aussi complet sans frais de location. Excellent."],
  de: ["AyaPay hat unsere Zahlungsabwicklung komplett verandert. Das kostenlose Terminal und der 24/7-Support machen den Unterschied.", "Seit wir AyaPay nutzen, sind unsere Transaktionen schneller und unsere Kunden zufrieden. Der 24h-Ersatz ist top.", "Die Integration mit unserer Kasse ist perfekt. Kein Problem seit der Installation, und die Swisscom 5G-SIM sorgt fur standige Verbindung.", "Das kostenlose Terminal mit allem inklusive hat uns sofort uberzeugt. Keine Uberraschungen auf der Rechnung.", "Tadelloser Service. Das AyaPay-Team ist immer erreichbar, auch am Wochenende. 100% Empfehlung.", "Wir haben mehrere Anbieter getestet. Keiner bietet so einen kompletten Service ohne Mietkosten. Ausgezeichnet."],
  it: ["AyaPay ha trasformato la gestione dei nostri pagamenti. Il terminale gratuito e il supporto 24/7 fanno la differenza.", "Da quando usiamo AyaPay, le transazioni sono piu veloci e i clienti soddisfatti. La sostituzione in 24h e fantastica.", "L'integrazione con la nostra cassa e perfetta. Zero problemi dall'installazione, e la SIM 5G Swisscom garantisce connessione permanente.", "Il terminale gratuito tutto incluso ci ha convinto subito. Nessuna sorpresa in fattura.", "Servizio impeccabile. Il team AyaPay e sempre disponibile, anche nel weekend. Consigliamo al 100%.", "Abbiamo provato diversi fornitori. Nessuno offre un servizio cosi completo senza costi di noleggio. Eccellente."],
  pt: ["AyaPay transformou a nossa gestao de pagamentos. O terminal gratuito e o suporte 24/7 fazem toda a diferenca.", "Desde que usamos AyaPay, as transacoes sao mais rapidas e os clientes estao satisfeitos. A substituicao em 24h e otima.", "A integracao com a nossa caixa e perfeita. Zero problemas desde a instalacao, e o SIM 5G Swisscom garante conexao permanente.", "O terminal gratuito tudo incluido convenceu-nos imediatamente. Sem surpresas na fatura.", "Servico impecavel. A equipa AyaPay esta sempre disponivel, mesmo ao fim de semana. Recomendamos a 100%.", "Testamos varios fornecedores. Nenhum oferece servico tao completo sem custos de aluguer. Excelente."],
  en: ["AyaPay transformed how we manage payments. The free terminal and 24/7 support make all the difference.", "Since using AyaPay, our transactions are faster and customers are happy. The 24h replacement is great.", "Integration with our register is perfect. Zero issues since installation, and the Swisscom 5G SIM ensures permanent connection.", "The free terminal with everything included convinced us immediately. No surprises on the bill.", "Impeccable service. The AyaPay team is always available, even on weekends. We recommend 100%.", "We tried several providers before AyaPay. None offers such a complete service without rental fees. Excellent."],
  tr: ["AyaPay odeme yonetimimizi tamamen degistirdi. Ucretsiz terminal ve 7/24 destek fark yaratiyor.", "AyaPay'i kullanmaya basladigimizdan beri islemlerimiz daha hizli ve musterilerimiz memnun. 24 saatte degisim harika.", "Kasamizla entegrasyon mukemmel. Kurulumdan beri sifir sorun, Swisscom 5G SIM surekli baglanti sagliyor.", "Her sey dahil ucretsiz terminal bizi hemen ikna etti. Faturada surpriz yok.", "Kusursuz hizmet. AyaPay ekibi hafta sonu bile her zaman mevcut. %100 tavsiye ediyoruz.", "AyaPay'dan once bircok saglayici denedik. Hicbiri kiralama ucreti olmadan bu kadar kapsamli hizmet sunmuyor. Mukemmel."],
};

export default function HomePage() {
  const { t, lang } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const FEATURES = [
    { icon: CreditCard, title: t("features.terminals"), desc: t("features.terminalsDesc") },
    { icon: Globe, title: t("features.online"), desc: t("features.onlineDesc") },
    { icon: LinkIcon, title: t("features.links"), desc: t("features.linksDesc") },
    { icon: Shield, title: t("features.security"), desc: t("features.securityDesc") },
    { icon: BarChart3, title: t("features.reports"), desc: t("features.reportsDesc") },
    { icon: Smartphone, title: t("features.mobile"), desc: t("features.mobileDesc") },
  ];

  const STATS = [
    { value: "10M+", label: t("stats.transactions") },
    { value: "48h", label: t("stats.install") },
    { value: "24/7", label: t("stats.support") },
  ];

  const texts = TESTIMONIALS_TEXT[lang] || TESTIMONIALS_TEXT.fr;

  return (
    <div data-testid="home-page">
      <SEO page="home" />
      <JsonLd data={[organizationSchema, localBusinessSchema, websiteSchema]} />
      <HeroCarousel />

      {/* Partners marquee */}
      <section className="py-8 bg-[#F8F9FA] border-y border-slate-100 overflow-hidden">
        <div className="flex items-center">
          <div className="animate-marquee partner-track">
            {[...Array(2)].map((_, setIdx) => (
              ["Visa", "Mastercard", "Twint", "Apple Pay", "Google Pay", "Samsung Pay", "PostFinance", "Amex"].map((name, i) => (
                <span key={`${setIdx}-${i}`} className="text-sm font-medium text-slate-400 whitespace-nowrap px-4">{name}</span>
              ))
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats-section" className="py-12 md:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            {STATS.map((stat, i) => (
              <AnimatedSection key={i} delay={i * 0.1} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B2B5B] tracking-tight">{stat.value}</div>
                <div className="text-xs md:text-sm text-[#475569] mt-1 md:mt-2">{stat.label}</div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Terminals */}
      <section data-testid="terminals-section" className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("terminals.tag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("terminals.title")}</h2>
            <p className="text-base text-[#475569] mt-4 max-w-lg mx-auto">{t("terminals.desc")}</p>
          </AnimatedSection>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {TERMINALS.map((term, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <Link to={`/terminaux/${term.slug}`} className="block">
                  <div className="feature-card bg-white rounded-xl border border-slate-100 p-4 md:p-6 text-center h-full flex flex-col">
                    <div className="flex-1 flex items-center justify-center mb-3 md:mb-4">
                      <img src={term.image} alt={`Terminal de paiement ${term.name} — AyaPay Suisse`} loading="lazy" className="h-32 sm:h-40 lg:h-56 object-contain" />
                    </div>
                    <span className="inline-block text-[10px] md:text-xs font-medium text-[#00C853] bg-[#00C853]/10 rounded-full px-2 md:px-3 py-0.5 mb-1.5 w-fit mx-auto">
                      {t(`terminals.${term.tagKey}`)}
                    </span>
                    <h3 className="text-sm md:text-lg font-semibold text-[#0A1128]">{term.name}</h3>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection className="text-center mt-10">
            <Link to="/solutions" data-testid="see-all-terminals" className="inline-flex items-center gap-2 font-semibold text-[#1B2B5B] border border-[#1B2B5B] rounded-full px-6 py-2.5 text-sm hover:bg-[#1B2B5B] hover:text-white transition-all">
              {t("terminals.seeAll")} <ArrowRight size={16} />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section data-testid="features-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("features.tag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("features.title")}</h2>
            <p className="text-base text-[#475569] mt-4 max-w-lg mx-auto">{t("features.desc")}</p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feat, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-200 p-8 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#00C853]/10 flex items-center justify-center mb-5">
                    <feat.icon size={22} className="text-[#00C853]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1128] mb-2">{feat.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{feat.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section data-testid="how-it-works" className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("steps.tag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("steps.title")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { num: "01", title: t("steps.step1"), desc: t("steps.step1Desc"), time: t("steps.step1Time") },
              { num: "02", title: t("steps.step2"), desc: t("steps.step2Desc"), time: t("steps.step2Time") },
              { num: "03", title: t("steps.step3"), desc: t("steps.step3Desc"), time: t("steps.step3Time") },
            ].map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.15}>
                <div className="relative">
                  <span className="text-6xl font-bold text-[#1B2B5B]/5">{step.num}</span>
                  <h3 className="text-xl font-semibold text-[#0A1128] mt-2">{step.title}</h3>
                  <p className="text-sm text-[#475569] mt-3 leading-relaxed">{step.desc}</p>
                  <span className="inline-block mt-4 text-xs font-medium text-[#00C853] bg-[#00C853]/10 rounded-full px-3 py-1">{step.time}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("testimonials.tag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("testimonials.title")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((te, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="bg-[#F8F9FA] rounded-xl border border-slate-100 p-8 h-full">
                  <p className="text-sm text-[#475569] leading-relaxed italic mb-6">"{texts[i]}"</p>
                  <div>
                    <div className="font-semibold text-sm text-[#0A1128]">{te.name}</div>
                    <div className="text-xs text-[#475569]">{te.role}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="py-16 md:py-24 lg:py-32 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">{t("cta.title")}</h2>
            <p className="text-sm md:text-base text-slate-300 mt-3 md:mt-4 max-w-md mx-auto">{t("cta.desc")}</p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-6 md:mt-8">
              <Link to="/contact" data-testid="cta-demo-button" className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">
                {t("cta.demo")} <ArrowRight size={16} />
              </Link>
              <Link to="/tarifs" data-testid="cta-pricing-button" className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all">
                {t("cta.pricing")}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
